/**
 * Scheduled slots (Promise Time Phase 2). The server owns the list (quote-eta `slots`:
 * operating hours, branch timezone, lead time, slot capacity); this module only groups,
 * labels and fetches it. Times come from `slotKey`, which is branch-local wall time.
 */
import { ApiError } from "@/api/http";
import type {
  QuoteEtaRequest,
  QuoteEtaResponse,
  ScheduledSlot,
} from "@/api/online-ordering-service/model";

export const SLOT_FULL = "SLOT_FULL";
export const PROMISE_ASAP_PAUSED = "PROMISE_ASAP_PAUSED";
const SLOT_MINUTES = 15;

export interface SlotDay {
  /** Branch-local date "YYYY-MM-DD" (the first 10 characters of slotKey). */
  key: string;
  slots: ScheduledSlot[];
}

/** A 409 body's fields, whether the BFF forwarded it as-is or wrapped it in `detail`. */
function conflictBody(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return null;
  const detail = body.detail;
  return detail && typeof detail === "object" ? (detail as Record<string, unknown>) : body;
}

export function isSlotFullError(err: unknown): boolean {
  return conflictBody(err)?.code === SLOT_FULL;
}

export function asapPausedNextSlot(err: unknown): string | null {
  const body = conflictBody(err);
  if (body?.code !== PROMISE_ASAP_PAUSED) return null;
  return typeof body.nextAvailableSlot === "string" ? body.nextAvailableSlot : null;
}

export function groupSlotsByDay(slots: ScheduledSlot[] | null | undefined): SlotDay[] {
  const days: SlotDay[] = [];
  for (const slot of slots ?? []) {
    const key = slot.slotKey.slice(0, 10);
    const last = days[days.length - 1];
    if (last && last.key === key) last.slots.push(slot);
    else days.push({ key, slots: [slot] });
  }
  return days;
}

function addMinutes(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = (h * 60 + m + minutes) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** Branch-local "HH:MM" start and end of a slot. */
export function slotRange(slot: ScheduledSlot): { from: string; to: string } {
  const from = slot.slotKey.slice(11, 16);
  return { from, to: addMinutes(from, SLOT_MINUTES) };
}

/** "Wed 16 Sep" for a branch-local day key, formatted in UTC so the device timezone cannot shift it. */
export function formatDayKey(key: string, locale?: string): string {
  return new Date(`${key}T12:00:00Z`).toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export async function fetchSlots(
  quote: (body: QuoteEtaRequest) => Promise<QuoteEtaResponse>,
  body: QuoteEtaRequest,
): Promise<ScheduledSlot[]> {
  try {
    const res = await quote(body);
    return res.slots ?? [];
  } catch (err) {
    const next = asapPausedNextSlot(err);
    if (!next) throw err;
    // ASAP is paused (Phase 1): a scheduled quote is never refused and carries the same slots.
    const res = await quote({ ...body, scheduledTimestamp: next });
    return res.slots ?? [];
  }
}

/** Checkout refetches the slot list once it is older than this (the server built it from "now"). */
export const SLOT_LIST_MAX_AGE_MS = 5 * 60_000;

/**
 * True when the slot still starts at or after `now + minLeadMinutes`. The server
 * builds the list from the lead at fetch time and order-service does not enforce
 * the lead on create, so the client re-applies the floor (`resolveMinLeadMinutes`)
 * when it renders and again before it submits.
 */
export function slotMeetsLead(slot: ScheduledSlot, minLeadMinutes: number, nowMs: number): boolean {
  const start = Date.parse(slot.start);
  if (Number.isNaN(start)) return false;
  return start >= nowMs + Math.max(0, minLeadMinutes) * 60_000;
}

/** The slots a customer may still pick now: the server list minus any inside the lead floor or past. */
export function bookableSlots(
  slots: ScheduledSlot[] | null | undefined,
  minLeadMinutes: number,
  nowMs: number,
): ScheduledSlot[] {
  return (slots ?? []).filter((s) => slotMeetsLead(s, minLeadMinutes, nowMs));
}

/** True when a slot list fetched at `fetchedAtMs` should be refetched. */
export function slotListIsStale(fetchedAtMs: number | null, nowMs: number): boolean {
  return fetchedAtMs !== null && nowMs - fetchedAtMs > SLOT_LIST_MAX_AGE_MS;
}

/**
 * The lead floor for filtering slots. It is `resolveMinLeadMinutes`'s value,
 * except while the quote is reloading (which happens on every slot pick), when
 * the last settled server lead for this branch is kept so the list does not
 * jump to the larger legacy floor and drop the customer's pick.
 */
export function slotLeadFloor(
  resolvedMinLead: number,
  quoteLoading: boolean,
  lastSettledServerLead: number | null,
): number {
  return quoteLoading && lastSettledServerLead !== null ? lastSettledServerLead : resolvedMinLead;
}
